import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import { getDeadlineInfo } from "@/lib/deadline";
import {
  ArrowRight, ArrowLeft, CheckCircle, Camera, Upload, XCircle,
  IdentificationCard, Warning, Copy, CalendarDots, CreditCard, Bank,
  CircleNotch, Trash,
} from "@phosphor-icons/react";

const DOC_TYPES = [
  { key: "id_front", label: "Kimlik Ön Yüz", icon: IdentificationCard, hint: "T.C. Kimlik kartınızın ön (fotoğraflı) yüzü" },
  { key: "id_back", label: "Kimlik Arka Yüz", icon: IdentificationCard, hint: "T.C. Kimlik kartınızın arka yüzü" },
  { key: "photo", label: "Biyometrik Fotoğraf", icon: Camera, hint: "Beyaz zeminde son 6 ay içinde çekilmiş vesikalık" },
];

function pretty(t) {
  return DOC_TYPES.find(d => d.key === t)?.label || t;
}

export default function ApplicationFlow() {
  const { rid } = useParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [reg, setReg] = useState(null);
  const [course, setCourse] = useState(null);
  const [step, setStep] = useState(1);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const deadline = getDeadlineInfo();

  // step 1
  const [plan, setPlan] = useState("");
  const [acceptDeadline, setAcceptDeadline] = useState(false);

  // step 2
  const [uploading, setUploading] = useState(null); // doc_type currently uploading

  useEffect(() => {
    if (!authLoading && !user) nav("/giris");
  }, [authLoading, user, nav]);

  const loadReg = async () => {
    try {
      const { data } = await api.get(`/registrations/${rid}`);
      setReg(data);
      // Fetch course
      const { data: courses } = await api.get("/courses");
      const c = courses.find(x => x.code === data.course_class);
      setCourse(c);
      // Auto-jump step
      if (data.payment?.plan && (!data.documents || data.documents.length < DOC_TYPES.length)) setStep(2);
      else if (data.documents && data.documents.length >= DOC_TYPES.length) setStep(3);
    } catch (e) {
      setErr("Başvuru bulunamadı");
    }
  };

  useEffect(() => { if (user) loadReg(); /* eslint-disable-next-line */ }, [user, rid]);

  const savePlan = async () => {
    if (!plan) { setErr("Bir ödeme planı seçin"); return; }
    setLoading(true); setErr("");
    try {
      const { data } = await api.post(`/registrations/${rid}/payment-plan`, {
        plan, accept_deadline: acceptDeadline,
      });
      setReg(prev => ({ ...prev, payment: data.payment, payment_method: plan }));
      setStep(2);
    } catch (e) { setErr(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setLoading(false); }
  };

  const uploadDoc = async (docType, file) => {
    setUploading(docType); setErr("");
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      await api.post(`/registrations/${rid}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadReg();
    } catch (e) { setErr(e.response?.data?.detail || "Yükleme başarısız"); }
    finally { setUploading(null); }
  };

  if (!reg) {
    return (
      <div className="max-w-2xl mx-auto py-20 text-center">
        {err ? <div className="text-brand-error">{err}</div> : <CircleNotch weight="bold" size={40} className="animate-spin mx-auto text-brand-primary"/>}
      </div>
    );
  }

  const totalPrice = course?.price || 0;
  const partial = Math.round(totalPrice * 0.2);
  const uploadedTypes = new Set((reg.documents || []).map(d => d.type));
  const allUploaded = DOC_TYPES.every(d => uploadedTypes.has(d.key));

  return (
    <>
      <SEO title="Başvurum | Estem Sürücü Kursu" canonical="" noindex/>
      <section className="px-4 md:px-8 py-8 md:py-12 max-w-4xl mx-auto">
        {/* header */}
        <div className="text-center mb-6">
          <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-brand-primary">Başvuru #{rid.slice(0,8).toUpperCase()}</div>
          <h1 className="font-heading font-bold text-2xl md:text-4xl mt-1">{reg.full_name}</h1>
          <div className="text-sm text-stone-500 mt-1">{course?.name} • Kayıt {new Date(reg.created_at).toLocaleDateString("tr-TR")}</div>
        </div>

        {/* deadline banner */}
        <div className={`rounded-xl px-4 py-3 mb-6 flex items-center gap-3 text-sm ${
          deadline.urgency === "critical" ? "bg-brand-error text-white" :
          deadline.urgency === "warning" ? "bg-orange-500 text-white" :
          "bg-brand-primary/10 text-brand-ink"
        }`} data-testid="deadline-banner">
          <Warning weight="fill" size={22}/>
          <div className="flex-1">
            <div className="font-bold">{deadline.badge}</div>
            <div className="text-xs opacity-90">{deadline.subline}</div>
          </div>
        </div>

        {/* stepper */}
        <div className="grid grid-cols-3 gap-2 mb-8">
          {[
            { n: 1, l: "Ödeme Planı", done: !!reg.payment?.plan },
            { n: 2, l: "Belgeler", done: allUploaded },
            { n: 3, l: "Onay", done: reg.status === "documents_received" || reg.status === "paid" || reg.status === "active" },
          ].map(s => (
            <div key={s.n} className={`p-3 rounded-lg border-2 text-center text-sm ${
              step === s.n ? "border-brand-primary bg-brand-primary/5" :
              s.done ? "border-brand-success bg-brand-success/10" : "border-stone-200 bg-white"
            }`} data-testid={`flow-step-${s.n}`}>
              <div className="flex items-center justify-center gap-1 font-bold">
                {s.done ? <CheckCircle weight="fill" className="text-brand-success" size={18}/> : <span>{s.n}</span>}
                <span>{s.l}</span>
              </div>
            </div>
          ))}
        </div>

        {err && <div className="mb-4 p-3 bg-brand-error/10 border border-brand-error/30 rounded-lg text-brand-error font-semibold text-sm">{err}</div>}

        {/* STEP 1: PAYMENT PLAN */}
        {step === 1 && (
          <div className="nb-card p-6 md:p-8" data-testid="payment-plan">
            <div className="font-heading font-bold text-xl mb-1">Ödeme Planınızı Seçin</div>
            <div className="text-sm text-stone-500 mb-6">{course?.name} kurs ücreti: <span className="font-bold text-brand-ink">{totalPrice.toLocaleString("tr-TR")} ₺</span></div>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {/* Kapora */}
              <label className={`p-5 border-2 rounded-xl cursor-pointer transition-all ${plan === "eft-20" ? "border-brand-primary bg-brand-primary/5" : "border-stone-200"}`} data-testid="plan-eft-20">
                <input type="radio" name="plan" value="eft-20" checked={plan === "eft-20"} onChange={e => setPlan(e.target.value)} className="sr-only"/>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-bold uppercase text-sm tracking-wide">Kapora ile Başla</div>
                  <Bank weight="bold" className="text-brand-primary" size={22}/>
                </div>
                <div className="text-3xl font-heading font-bold text-brand-primary">{partial.toLocaleString("tr-TR")} ₺</div>
                <div className="text-xs text-stone-500 mt-1">Şimdi öde · Kalan {(totalPrice-partial).toLocaleString("tr-TR")} ₺ kayıt tamamlanınca</div>
                <ul className="mt-3 space-y-1 text-xs text-stone-600">
                  <li>✓ EFT/Havale ile ödeme</li>
                  <li>✓ Yerinizi hemen bloke et</li>
                  <li>✓ %20 ön ödemeyle başlayın</li>
                </ul>
              </label>
              {/* Tam ödeme */}
              <label className={`p-5 border-2 rounded-xl cursor-pointer transition-all ${plan === "eft-full" ? "border-brand-primary bg-brand-primary/5" : "border-stone-200"}`} data-testid="plan-eft-full">
                <input type="radio" name="plan" value="eft-full" checked={plan === "eft-full"} onChange={e => setPlan(e.target.value)} className="sr-only"/>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-bold uppercase text-sm tracking-wide">Tam Ödeme</div>
                  <span className="text-[10px] px-2 py-0.5 bg-brand-success text-white rounded-full font-bold">ÖNERİLEN</span>
                </div>
                <div className="text-3xl font-heading font-bold text-brand-primary">{totalPrice.toLocaleString("tr-TR")} ₺</div>
                <div className="text-xs text-stone-500 mt-1">Tek seferde ödeme · Sonradan ek yok</div>
                <ul className="mt-3 space-y-1 text-xs text-stone-600">
                  <li>✓ EFT/Havale ile ödeme</li>
                  <li>✓ Kayıt anında kesinleşir</li>
                  <li>✓ Öncelikli program yerleştirme</li>
                </ul>
              </label>
            </div>

            {/* Iyzico placeholder */}
            <div className="p-4 border-2 border-dashed border-stone-200 rounded-xl text-center text-sm text-stone-500 mb-6">
              <CreditCard weight="bold" className="inline-block mr-1" size={18}/>
              Kredi kartı ile online ödeme (Iyzico) — <span className="font-semibold text-brand-primary">yakında</span>
            </div>

            <label className="flex items-start gap-2 mb-5 p-3 bg-brand-primary/5 border border-brand-primary/20 rounded-lg text-sm">
              <input type="checkbox" checked={acceptDeadline} onChange={e => setAcceptDeadline(e.target.checked)} className="mt-1 w-4 h-4 accent-brand-primary" data-testid="accept-deadline"/>
              <span>Belgelerimi ve ödememi <b>{deadline.monthName} ayının 8&apos;ine kadar</b> teslim etmeyi kabul ediyorum. Aksi hâlde başvurum bir sonraki döneme aktarılacaktır.</span>
            </label>

            <button disabled={loading || !plan || !acceptDeadline} onClick={savePlan} className="nb-btn w-full disabled:opacity-40 disabled:cursor-not-allowed" data-testid="save-plan-btn">
              {loading ? "Kaydediliyor…" : "Devam Et"} <ArrowRight weight="bold"/>
            </button>
          </div>
        )}

        {/* STEP 2: DOCUMENTS */}
        {step === 2 && (
          <div className="nb-card p-6 md:p-8" data-testid="docs-step">
            <div className="font-heading font-bold text-xl mb-1">Belgelerinizi Yükleyin</div>
            <div className="text-sm text-stone-500 mb-6">3 belgeyi yükleyin. Kamera ile çekebilir veya galeriden seçebilirsiniz.</div>

            {/* Payment reference (if EFT) */}
            {reg.payment?.method === "eft" && (
              <div className="mb-6 p-4 rounded-xl bg-brand-primary/5 border border-brand-primary/20" data-testid="payment-ref">
                <div className="flex items-center gap-2 mb-3">
                  <Bank weight="bold" className="text-brand-primary" size={22}/>
                  <div className="font-bold">EFT / Havale Bilgileri</div>
                </div>
                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-stone-500">IBAN</div>
                    <div className="font-mono font-bold">TR00 0000 0000 0000 0000 0000 00</div>
                    <div className="text-xs text-stone-500 mt-2">Alıcı</div>
                    <div className="font-semibold">Estem Sürücü Kursu Tic. Ltd.</div>
                  </div>
                  <div>
                    <div className="text-xs text-stone-500">Açıklama (referans no)</div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="font-mono font-bold text-lg text-brand-primary" data-testid="eft-reference">{reg.payment.reference_no}</div>
                      <button onClick={() => navigator.clipboard.writeText(reg.payment.reference_no)} className="p-1 text-brand-primary hover:opacity-70" title="Kopyala"><Copy weight="bold" size={16}/></button>
                    </div>
                    <div className="text-xs text-stone-500 mt-2">Tutar</div>
                    <div className="font-bold text-lg">{reg.payment.amount_due.toLocaleString("tr-TR")} ₺</div>
                  </div>
                </div>
                <div className="text-xs text-stone-500 mt-3 border-t pt-2">Ödemenizi yaptıktan sonra ekibimiz 1 iş günü içinde onaylayacaktır. Açıklama alanına yukarıdaki referans numarasını yazmayı unutmayın.</div>
              </div>
            )}

            <div className="space-y-3">
              {DOC_TYPES.map(dt => {
                const uploaded = (reg.documents || []).find(d => d.type === dt.key);
                const isUp = uploading === dt.key;
                return (
                  <DocRow key={dt.key} dt={dt} uploaded={uploaded} isUploading={isUp} onFile={(f) => uploadDoc(dt.key, f)} rid={rid} onDeleted={loadReg}/>
                );
              })}
            </div>

            <div className="mt-6 flex gap-3">
              <button onClick={() => setStep(1)} className="nb-btn-outline" data-testid="docs-back"><ArrowLeft weight="bold"/> Geri</button>
              <button disabled={!allUploaded} onClick={() => setStep(3)} className="nb-btn flex-1 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="docs-next">
                {allUploaded ? "Devam Et" : `${DOC_TYPES.length - uploadedTypes.size} belge kaldı`} <ArrowRight weight="bold"/>
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: SUMMARY */}
        {step === 3 && (
          <div className="nb-card p-6 md:p-8" data-testid="summary-step">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-brand-success/10 mx-auto flex items-center justify-center mb-3">
                <CheckCircle weight="fill" size={40} className="text-brand-success"/>
              </div>
              <div className="font-heading font-bold text-2xl">Başvurunuz Tamamlandı</div>
              <div className="text-sm text-stone-500 mt-1">Aşağıdaki bilgileri onaylayın; süreç ekibimize düştü.</div>
            </div>

            <div className="space-y-3 text-sm">
              <SummaryRow label="Kurs Sınıfı" value={course?.name}/>
              <SummaryRow label="Ad Soyad" value={reg.full_name}/>
              <SummaryRow label="Telefon" value={reg.phone}/>
              <SummaryRow label="E-posta" value={reg.email}/>
              <SummaryRow label="Ödeme Planı" value={
                reg.payment?.type === "partial"
                  ? `%20 Kapora — ${reg.payment.amount_due.toLocaleString("tr-TR")} ₺`
                  : `Tam Ödeme — ${reg.payment?.amount_due?.toLocaleString("tr-TR") || totalPrice} ₺`
              }/>
              {reg.payment?.reference_no && <SummaryRow label="EFT Referansı" value={reg.payment.reference_no} mono/>}
              <SummaryRow label="Yüklenen Belgeler" value={`${(reg.documents || []).length} / ${DOC_TYPES.length} tamamlandı`}/>
              <SummaryRow label="Kayıt Kapanış" value={`${deadline.monthName} 8`} icon={CalendarDots}/>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-brand-primary/5 border border-brand-primary/20 text-sm">
              <div className="font-bold mb-1">Sırada Ne Var?</div>
              <ol className="list-decimal list-inside space-y-1 text-stone-600">
                <li>Ödemenizi EFT ile yapın (varsa)</li>
                <li>Ekibimiz belgelerinizi inceleyecek</li>
                <li>Panel üzerinden ders programınızı göreceksiniz</li>
                <li>{deadline.monthName} 10&apos;da eğitim başlıyor</li>
              </ol>
            </div>

            <button onClick={() => nav("/panelim")} className="nb-btn w-full mt-6" data-testid="go-dashboard">
              Panele Git <ArrowRight weight="bold"/>
            </button>
          </div>
        )}
      </section>
    </>
  );
}

function SummaryRow({ label, value, icon: Icon, mono }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-stone-100">
      <div className="text-stone-500 flex items-center gap-1.5">{Icon && <Icon weight="bold" size={16}/>} {label}</div>
      <div className={`font-semibold ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function DocRow({ dt, uploaded, isUploading, onFile, rid, onDeleted }) {
  const Icon = dt.icon;
  const [showCam, setShowCam] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
  };

  return (
    <div className={`p-4 rounded-xl border-2 ${uploaded ? "border-brand-success/40 bg-brand-success/5" : "border-stone-200 bg-white"}`} data-testid={`doc-row-${dt.key}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Icon weight="bold" size={22} className={uploaded ? "text-brand-success" : "text-stone-500"}/>
          <div>
            <div className="font-bold">{dt.label}</div>
            <div className="text-xs text-stone-500">{dt.hint}</div>
          </div>
        </div>
        {uploaded && <CheckCircle weight="fill" size={22} className="text-brand-success"/>}
      </div>

      {uploaded ? (
        <div className="flex items-center gap-2 text-xs mt-2">
          <a href={uploaded.url.startsWith("http") ? uploaded.url : uploaded.url} target="_blank" rel="noreferrer" className="text-brand-primary underline">Yüklenen dosyayı görüntüle</a>
          <span className="text-stone-400">·</span>
          <span className="text-stone-500">{uploaded.filename}</span>
        </div>
      ) : isUploading ? (
        <div className="flex items-center gap-2 text-xs text-brand-primary mt-2"><CircleNotch weight="bold" className="animate-spin" size={14}/> Yükleniyor…</div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={() => setShowCam(true)} className="nb-btn-outline !text-xs !py-2" data-testid={`doc-cam-${dt.key}`}>
            <Camera weight="bold" size={16}/> Kamera ile Çek
          </button>
          <label className="nb-btn-outline !text-xs !py-2 cursor-pointer" data-testid={`doc-upload-${dt.key}`}>
            <Upload weight="bold" size={16}/> Dosya Yükle
            <input type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden"/>
          </label>
        </div>
      )}

      {showCam && <CameraModal docType={dt.key} onClose={() => setShowCam(false)} onCapture={(f) => { setShowCam(false); onFile(f); }}/>}
    </div>
  );
}

function CameraModal({ docType, onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [err, setErr] = useState("");
  const isID = docType === "id_front" || docType === "id_back";

  useEffect(() => {
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        setStream(s);
        if (videoRef.current) { videoRef.current.srcObject = s; }
      } catch (e) {
        setErr("Kamera açılamadı. Lütfen izin verin veya dosya yükleme seçeneğini kullanın.");
      }
    })();
    return () => { if (stream) stream.getTracks().forEach(t => t.stop()); };
    // eslint-disable-next-line
  }, []);

  const shoot = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `${docType}.jpg`, { type: "image/jpeg" });
        if (stream) stream.getTracks().forEach(t => t.stop());
        onCapture(file);
      }
    }, "image/jpeg", 0.92);
  };

  const close = () => {
    if (stream) stream.getTracks().forEach(t => t.stop());
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="camera-modal">
      <div className="flex items-center justify-between p-4 text-white">
        <div className="font-bold">{pretty(docType)} - Fotoğraf Çekimi</div>
        <button onClick={close} className="p-2" data-testid="camera-close"><XCircle weight="bold" size={28}/></button>
      </div>
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {err ? (
          <div className="text-white text-center px-6">{err}</div>
        ) : (
          <>
            <video ref={videoRef} autoPlay playsInline muted className="max-w-full max-h-full"/>
            {/* Silhouette overlay */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-6">
              {isID ? (
                <div className="border-4 border-brand-primary rounded-2xl w-full max-w-xl aspect-[85/54] shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]">
                  <div className="text-center text-white text-xs mt-2 font-bold uppercase tracking-widest">Kimliği bu çerçeveye hizalayın</div>
                </div>
              ) : (
                <div className="border-4 border-brand-primary rounded-full w-56 h-72 shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]"/>
              )}
            </div>
          </>
        )}
        <canvas ref={canvasRef} className="hidden"/>
      </div>
      <div className="p-4 flex justify-center bg-black">
        {!err && (
          <button onClick={shoot} className="w-16 h-16 rounded-full bg-white border-4 border-brand-primary flex items-center justify-center" data-testid="camera-shoot">
            <div className="w-12 h-12 rounded-full bg-brand-primary"/>
          </button>
        )}
      </div>
    </div>
  );
}
